<template>
  <jr-button class="save-btn" type="primary" @click="save"> OK </jr-button>
</template>

<script lang="ts">
import { defineComponent, PropType, Ref } from 'vue'
import { ElForm } from 'element-plus'

import GeneralAttachedFiles from '@/components/inputs/GeneralAttachedFiles.vue'
import JrButton from '@/components/jr-parts/JrButton.vue'
import JrFileInput from '@/components/jr-parts/JrFileInput.vue'

import { CreateRequest, UpdateRequest } from '@/requests/boundary'
import { RequestHeaderBoundaryDetailForm } from '@/types/forms'
import { JrBoundary, RequestHeaderJrInput } from '@/api'
import { startLoading, endLoading } from '@/plugins/loading'
import { RequestReasonTypes } from '@/constants/enums'

/**
 * 申請基本情報から送信用境界立会情報に変換する
 */
const requestHeaderToBoundary = (
  requestHeaderDetail: RequestHeaderBoundaryDetailForm,
  historyComment: string
): JrBoundary => {
  const requestHeader: RequestHeaderJrInput = {
    applicant_business_name: requestHeaderDetail.applicant_business_name,
    applicant_email: requestHeaderDetail.applicant_email,
    applicant_manager_name: requestHeaderDetail.applicant_manager_name,
    applicant_address: requestHeaderDetail.applicant_address,
    applicant_tel: requestHeaderDetail.applicant_tel,
    department_code: requestHeaderDetail.department_code as string,
    department_name: requestHeaderDetail.department_name as string,
    department: requestHeaderDetail.department as number,
    applicant_title: requestHeaderDetail.applicant_title as string,
    meter: requestHeaderDetail.meter,
    direction: requestHeaderDetail.direction,
    general_attached_file: requestHeaderDetail.general_attached_file,
  }

  const boundary: JrBoundary = {
    request_header: requestHeader,
    history_comment: historyComment,
    boundary_id: requestHeaderDetail.boundary.boundary_id,
    agent_address: requestHeaderDetail.boundary.agent_address,
    agent_corporate_name: requestHeaderDetail.boundary.agent_corporate_name,
    agent_name: requestHeaderDetail.boundary.agent_name,
    contact_tel: requestHeaderDetail.boundary.contact_tel,
    request_reason_type: requestHeaderDetail.boundary.request_reason_type,
    request_reason_type_other: requestHeaderDetail.boundary.request_reason_type_other,
    is_boundary_marker_confirm: requestHeaderDetail.boundary.is_boundary_marker_confirm,
    is_boundary_marker_restore: requestHeaderDetail.boundary.is_boundary_marker_restore,
    is_boundary_fixed_surveying: requestHeaderDetail.boundary.is_boundary_fixed_surveying,
    location: requestHeaderDetail.boundary.location,
    parcel_number: requestHeaderDetail.boundary.parcel_number,
    land_category: requestHeaderDetail.boundary.land_category,
    parcel_area: requestHeaderDetail.boundary.parcel_area,
    line_name: requestHeaderDetail.boundary.line_name,
    start_station_name: requestHeaderDetail.boundary.start_station_name,
    end_station_name: requestHeaderDetail.boundary.end_station_name,
    latitude: requestHeaderDetail.boundary.latitude,
    longitude: requestHeaderDetail.boundary.longitude,
    jr_site_location: requestHeaderDetail.boundary.jr_site_location,
    jr_site_parcel_number: requestHeaderDetail.boundary.jr_site_parcel_number,
    line: requestHeaderDetail.boundary.line,
    start_station: requestHeaderDetail.boundary.start_station,
    end_station: requestHeaderDetail.boundary.end_station,
    boundary_application_form_file: requestHeaderDetail.boundary.boundary_application_form_file,
    identification_file: requestHeaderDetail.boundary.identification_file,
    power_of_attorney_file: requestHeaderDetail.boundary.power_of_attorney_file,
    location_map_file: requestHeaderDetail.boundary.location_map_file,
    ground_plan_file: requestHeaderDetail.boundary.ground_plan_file,
    survey_drawing_of_acreage_file: requestHeaderDetail.boundary.survey_drawing_of_acreage_file,
    cadastral_map_file: requestHeaderDetail.boundary.cadastral_map_file,
    certificate_of_registered_matters_file: requestHeaderDetail.boundary.certificate_of_registered_matters_file,
    actual_place_photo_file: requestHeaderDetail.boundary.actual_place_photo_file,
  }

  // データ内容の編集
  if (boundary.request_header.applicant_email === '') {
    // メールアドレス未入力時はnullにする
    boundary.request_header.applicant_email = null
  }
  if (boundary.request_reason_type !== RequestReasonTypes.OTHER.code) {
    // 申請理由がその他以外の場合、申請理由その他を空文字で登録
    boundary.request_reason_type_other = ''
  }

  return boundary
}

/**
 * 境界立会申請の保存ボタン
 */
export default defineComponent({
  name: 'BoundarySave',
  emits: ['sent'],
  components: {
    JrButton,
  },
  props: {
    requestHeader: {
      type: Object as PropType<RequestHeaderBoundaryDetailForm>,
      required: true,
    },
    historyComment: {
      type: String,
      required: true,
    },
    boundaryId: {
      type: Number,
      required: false,
      default: null,
    },
    formRef: {
      type: Object as PropType<InstanceType<typeof ElForm>>,
      required: false,
      default: null,
    },
    // ファイルアップロードコンポーネントref
    fileRefs: {
      type: Array as PropType<Array<Ref<InstanceType<typeof JrFileInput>>>>,
      required: true,
    },
    // その他ファイルコンポーネント
    generalAttachedFilesRef: {
      type: Object as PropType<InstanceType<typeof GeneralAttachedFiles>>,
      required: false,
      default: null,
    },
  },
  setup(props, context) {
    const save = async () => {
      if (!props.formRef || !props.generalAttachedFilesRef) {
        // DOM未生成時は何もしない
        return
      }
      props.formRef.validate(async (valid) => {
        if (!valid) {
          return
        }
        const loadingKey = 'boundary save'
        startLoading(loadingKey)
        const cancelLoadingCallback = () => {
          endLoading(loadingKey)
        }
        // ファイルアップロード
        for (const ref of props.fileRefs) {
          await ref.value.uploadFile(cancelLoadingCallback)
        }
        // その他ファイルアップロード
        await props.generalAttachedFilesRef.allUpload(cancelLoadingCallback)
        // リクエスト内容を変換
        const boundary = requestHeaderToBoundary(props.requestHeader, props.historyComment)
        // IDがあれば更新、無ければ新規登録
        const request =
          props.boundaryId != null ? new UpdateRequest(props.boundaryId, boundary) : new CreateRequest(boundary)
        // 送信実行
        if (await request.exec()) {
          context.emit('sent')
        }
        endLoading(loadingKey)
      })
    }

    return {
      save,
    }
  },
})
</script>
